import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, DatePicker, Flex, Input, Select, Space } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useState } from 'react';
dayjs.extend(customParseFormat);
import { loanConfig } from '~/constants';
import type { Partner } from '~/types/auth';
import {
  type LoanFiltersType,
  type StatusTypes,
  journeyStatusOptions,
} from '~/types/loans';
import { dateFormat, filtersKey } from '~/utils/constants';
import notify from '~/utils/useNotification';
import { isDateRangeWithinLimit } from '~/utils/utilities';
import PartnerReferrersSelect from '../PartnerReferrersSelect/PartnerReferrersSelect';

interface DownloadCSVButtonProps {
  onDownloadCSV: () => Promise<void>;
  isDownloadDisabled?: boolean;
}

const DownloadCSVButton = ({
  onDownloadCSV,
  isDownloadDisabled = false,
}: DownloadCSVButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const handleDownloadClick = async () => {
    if (!onDownloadCSV || isDownloading) {
      return;
    }

    setIsDownloading(true);
    setDownloadSuccess(false);

    try {
      await onDownloadCSV();
      // Show success indicator for 500ms
      setDownloadSuccess(true);
      setTimeout(() => {
        setDownloadSuccess(false);
      }, 500);
    } catch {
      // Error handling is done in Dashboard component
      // Just reset loading state here
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      type="default"
      icon={downloadSuccess ? <CheckCircleOutlined /> : <DownloadOutlined />}
      onClick={handleDownloadClick}
      disabled={isDownloadDisabled || isDownloading}
      loading={isDownloading}
      className="rounded-none"
    >
      Download CSV
    </Button>
  );
};

interface LoanFiltersProps {
  resetFilters: () => void;
  filters: LoanFiltersType;
  setFilters: (filters: LoanFiltersType) => void;
  fetchLoans: (filters: LoanFiltersType) => Promise<boolean>;
  partners: Partner[];
  onDownloadCSV?: () => Promise<void>;
  isDownloadDisabled?: boolean;
}

const LoanFilters = ({
  resetFilters,
  filters,
  setFilters,
  fetchLoans,
  partners,
  onDownloadCSV,
  isDownloadDisabled = false,
}: LoanFiltersProps) => {
  const handleDateChange = (field: string, date: dayjs.Dayjs | null) => {
    const newFilters = {
      ...filters,
      [field]: date ? date.format(dateFormat) : '',
    };
    setFilters(newFilters);
    sessionStorage.setItem(filtersKey, JSON.stringify(newFilters));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = value;

    /* Allow only 10 characters for PAN */
    if (name === 'pan') {
      newValue = value.toUpperCase();
      if (newValue.length > 10) {
        return;
      }
    }

    /* Allow only numbers and prevent input if length exceeds 10 digits */
    if (name === 'phone') {
      if (/^\d*$/.test(value) && value.length <= 10) {
        newValue = value;
      } else {
        return;
      }
    }

    const newFilters = { ...filters, [name]: newValue };
    setFilters(newFilters);
    sessionStorage.setItem(filtersKey, JSON.stringify(newFilters));
  };

  const handleStatusChange = (value: StatusTypes[]) => {
    const newFilters = { ...filters, status: value };
    setFilters(newFilters);
    sessionStorage.setItem(filtersKey, JSON.stringify(newFilters));
  };

  /* Disable future dates and dates after toDate */
  const disableDateForFromDate = (current: Dayjs | null): boolean => {
    const customDate = dayjs();
    if (!current) return false;
    return Boolean(
      current.isAfter(customDate, 'day') || // Disable future dates
        (filters.toDate &&
          current.isAfter(dayjs(filters.toDate, dateFormat), 'day')),
    );
  };

  /* Disable future dates and dates before fromDate */
  const disableDateForToDate = (current: Dayjs | null): boolean => {
    const customDate = dayjs();
    if (!current) return false;
    return Boolean(
      current.isAfter(customDate, 'day') || // Disable future dates
        (filters.fromDate &&
          current.isBefore(dayjs(filters.fromDate, dateFormat), 'day')),
    );
  };

  const fetchLoansOnClick = async () => {
    if (
      !isDateRangeWithinLimit(
        filters.fromDate,
        filters.toDate,
        loanConfig.maxLoanFilterDateRangeInMonths,
      )
    ) {
      notify({
        type: 'warning',
        title: 'Date range exceeded',
        description: `Date range should not exceed ${loanConfig.maxLoanFilterDateRangeInMonths} months`,
        duration: 3,
      });
      return;
    }
    const updatedFilters = { ...filters, page: 1 };
    const success = await fetchLoans(updatedFilters);
    if (success) {
      setFilters(updatedFilters);
      sessionStorage.setItem(filtersKey, JSON.stringify(updatedFilters));
    }
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <Flex className="gap-4 ">
        <Input
          allowClear
          name="pan"
          value={filters.pan}
          placeholder="Filter applications by PAN"
          className="rounded-none"
          onChange={handleFilterChange}
        />
        <Input
          allowClear
          name="phone"
          value={filters.phone}
          placeholder="Filter applications by Phone"
          className="rounded-none"
          onChange={handleFilterChange}
        />
        <Input
          allowClear
          name="loanApplicationNumber"
          value={filters.loanApplicationNumber}
          placeholder="Filter applications by loan number"
          className="rounded-none"
          onChange={handleFilterChange}
        />
      </Flex>
      <Flex>
        <Select
          mode="multiple"
          allowClear
          className="w-full"
          placeholder="Select statuses"
          value={filters.status}
          onChange={handleStatusChange}
          options={journeyStatusOptions}
        />
      </Flex>
      <PartnerReferrersSelect
        partners={partners}
        filters={filters}
        setFilters={setFilters}
      />
      <div className="flex items-center gap-4">
        <Space className="gap-1">
          <DatePicker
            name="fromDate"
            size="middle"
            defaultValue={
              (filters.fromDate && dayjs(filters.fromDate, dateFormat)) ||
              undefined
            }
            format={dateFormat}
            showNow
            onChange={(date: Dayjs | null) =>
              handleDateChange('fromDate', date)
            }
            value={
              filters.fromDate ? dayjs(filters.fromDate, dateFormat) : undefined
            }
            allowClear={false}
            disabledDate={disableDateForFromDate}
          />
          <span className="mx-2">to</span>
          <DatePicker
            name="toDate"
            size="middle"
            defaultValue={
              (filters.toDate && dayjs(filters.toDate, dateFormat)) || undefined
            }
            format={dateFormat}
            showNow
            onChange={(date: Dayjs | null) => handleDateChange('toDate', date)}
            value={
              filters.toDate ? dayjs(filters.toDate, dateFormat) : undefined
            }
            allowClear={false}
            disabledDate={disableDateForToDate}
          />
        </Space>
        <Button
          type="primary"
          onClick={fetchLoansOnClick}
          className="rounded-none w-[120px]"
        >
          Search
        </Button>
        <Button
          type="default"
          onClick={() => resetFilters()}
          className="rounded-none w-[150px]"
        >
          Clear all filters
        </Button>
        {onDownloadCSV ? (
          <DownloadCSVButton
            onDownloadCSV={onDownloadCSV}
            isDownloadDisabled={isDownloadDisabled}
          />
        ) : null}
      </div>
    </div>
  );
};

export default LoanFilters;
